// @flow

import React from 'react';
import { padding } from 'polished';
import styled from '@emotion/styled';

import LabelsContent from 'app/components/static/LabelsContent';
import Tappable from 'app/components/commons/Tappable';
import type { TabItem } from 'app/types';

import SettingsContent from './SettingsContent';
import TeamsContent from './TeamsContent';
import FloorCeilingContent from './FloorCeilingContent';
import ImportExportContent from './ImportExportContent';
import SaveLoadProjectionContent from './SaveLoadProjectionContent';

type Props = {
  slateId: string | number,
  bonuses: Object,
  activeTabItem: ?TabItem,
  isLoading?: boolean,
  sportHasTeam?: boolean,
  useAverage?: boolean,
  labels: Array<Object>,
  activeSettingsTab: string,
  onClose?: () => void,
  onSelectSettingsTab: (tab: string) => void,
  scheme: string
};

const defaultProps = {
  sportHasTeam: true,
  useAverage: false,
  isLoading: false,
  onClose: undefined
};

const Wrapper = styled.div(({ theme }) => ({
  paddingBottom: theme.spacing.xsmall
}));

const CollapsibleBoxInner = styled.div(({ theme }) => ({
  paddingTop: theme.spacing.default,
  paddingBottom: theme.spacing.large,
  overflow: 'hidden',
  fontSize: theme.fontSize.small,
  ...padding(theme.spacing.small, theme.spacing.default),
  [theme.mediaQuery.desktopAndUp]: {
    ...padding(theme.spacing.default, theme.spacing.xxlarge)
  }
}));

const CollapseBoxTrigger = styled.div(
  {
    alignItems: 'center',
    borderRadius: 20,
    borderStyle: 'solid',
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
    textTransform: 'uppercase',
    width: 36
  },
  ({ theme }) => ({
    borderColor: theme.color.basis4,
    fontSize: theme.fontSize.xtiny
  })
);

const TabContent = ({
  activeTabItem,
  activeSettingsTab,
  bonuses,
  labels,
  isLoading,
  sportHasTeam,
  useAverage,
  onClose,
  onSelectSettingsTab,
  scheme,
  slateId
}: Props) => (
  <Wrapper>
    <CollapsibleBoxInner>
      {activeTabItem === 'teams' && <TeamsContent />}
      {activeTabItem === 'settings' && (
        <SettingsContent
          activeSettingsTab={activeSettingsTab}
          onSelectSettingsTab={onSelectSettingsTab}
          sportHasTeam={sportHasTeam}
          scheme={['white', 'light'].includes(scheme) ? 'light' : 'dark'}
        />
      )}
      {activeTabItem === 'labels' && (
        <LabelsContent
          key="labels"
          bonuses={bonuses}
          isLoading={isLoading}
          labels={labels}
          placeholder="Please select a slate"
        />
      )}
      {activeTabItem === 'floorCeiling' && <FloorCeilingContent />}
      {activeTabItem === 'importExport' && <ImportExportContent useAverage={useAverage} />}
      {activeTabItem === 'saveLoadProjection' && <SaveLoadProjectionContent slateId={slateId} />}
    </CollapsibleBoxInner>
    {onClose && (
      <Tappable onTap={onClose} component={CollapseBoxTrigger}>
        <span className="ion-chevron-up" />
      </Tappable>
    )}
  </Wrapper>
);

TabContent.defaultProps = defaultProps;

export default TabContent;
